<template>
  <div class="swof-tab-overview">
    <v-card flat class="transparent">
      <!-- Country Tabs -->
      <v-tabs v-model="tab" class="rounded-t-lg elevation-1" background-color="white">
        <v-tab v-for="(country, idx) in countries" :key="idx" @click="initCountrySwof(idx, country)">
          {{ country.name }}
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab" class="transparent mt-4">
        <v-tab-item v-for="(country, idx) in countries" :key="idx">
          <v-card v-if="areAllValuesEmpty(country.swof)" class="pa-6 text-center shadow-sm">
            <v-icon size="64" color="grey lighten-2">{{ icons.mdiAutoFix }}</v-icon>
            <div class="text-h6 grey--text text--darken-2 my-4">No SWOF generated for {{ country.name }}</div>
            
            <v-row dense class="max-width-800 mx-auto text-left">
              <v-col v-for="field in generatorFields" :key="field.model" cols="12" sm="4">
                <v-text-field
                  v-model="country[field.model]"
                  :label="field.label"
                  :hint="field.hint"
                  persistent-hint outlined dense type="number"
                />
              </v-col>
            </v-row>
            
            <v-btn color="primary" class="mt-8" x-large @click="generateSWOF(idx)">
              Generate Studio Work Order
            </v-btn>
          </v-card>

          <template v-else>
            <v-card class="mb-4">
              <v-toolbar flat dense class="grey lighten-4">
                <v-toolbar-title class="subtitle-1 font-weight-bold">SWOF Dashboard - {{ country.name }}</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn icon color="success" @click="downloadSWOF(country.swof.id)"><v-icon>{{icons.mdiFileExcelOutline}}</v-icon></v-btn>
                <v-btn icon color="error" @click="deleteSWOFConfirmation"><v-icon>{{icons.mdiDeleteOutline}}</v-icon></v-btn>
              </v-toolbar>
              
              <v-card-text class="d-flex flex-wrap align-center">
                <div class="me-6">Total Shelf Images: <strong>{{ country.swof.shelf_count }}</strong></div>
                <v-divider vertical class="mx-4" />
                <!-- Folder Links -->
                <div class="d-flex flex-wrap">
                    <v-btn 
                        v-for="folder in folderLinks" :key="folder.key"
                        small class="ma-1"
                        :color="folder.color"
                        :depressed="!!country.swof[folder.urlKey]"
                        :outlined="!country.swof[folder.urlKey]"
                        :class="{'white--text': !!country.swof[folder.urlKey]}"
                        @click="handleFolderClick(country.swof, folder.urlKey, folder.type)"
                    >
                        <v-icon left x-small>
                        {{ country.swof[folder.urlKey] ? icons.mdiOpenInNew : icons.mdiPlus }}
                        </v-icon>
                        {{ folder.label }}
                    </v-btn>
                    </div>
                <v-spacer />
                <v-btn color="primary" depressed @click="notificationOpen = true">
                  <v-icon left>{{ icons.mdiSendOutline }}</v-icon> Notify Team
                </v-btn>
              </v-card-text>
            </v-card>

            <v-card v-for="cat in activeCategories" :key="cat.key" class="mb-6 shadow-sm border">
              <v-toolbar flat dense color="white" class="border-bottom">
                <v-icon left small color="primary">{{ icons.mdiImageMultiple }}</v-icon>
                <v-toolbar-title class="body-2 font-weight-bold">
                  <a :href="country.swof[cat.urlKey]" target="_blank">{{ cat.label }}</a>
                </v-toolbar-title>
                
                <v-spacer></v-spacer>

                <v-btn x-small color="secondary" text @click="openSWOFSharepoint(country.swof.id, cat.spType)">Set Folder</v-btn>
                <v-divider vertical class="mx-2" />
                <v-btn x-small color="primary" depressed :disabled="!isAnySelected(country.swof, cat.key)" @click="editSelected(country.swof[cat.selectionKey])">Edit Selection</v-btn>
                
                <v-btn 
                  v-if="cat.duplicateAction"
                  x-small color="info" depressed class="ml-2"
                  :disabled="!isAnySelected(country.swof, cat.key)"
                  @click="duplicateSelection(country, cat.key, cat.duplicateAction.suffix, country.swof[cat.selectionKey])"
                >
                  {{ cat.duplicateAction.label }}
                </v-btn>
                
                <v-btn v-if="cat.key === 'aidedRecall'" x-small color="info" depressed class="ml-2" @click="setARTestDesign(country, country.swof.aidedRecallSelected)">Set AR Current/Test</v-btn>
              </v-toolbar>

              <v-data-table
                dense
                :headers="imageHeaders"
                :items="country.swof[cat.key]"
                v-model="country.swof[cat.selectionKey]"
                show-select
                hide-default-footer
                disable-pagination
              >
                <!-- Table Slots -->
                <template #[`item.label`]="{ item }">
                  <a v-if="item.url" :href="item.url" target="_blank" class="font-weight-medium">{{ item.label }}</a>
                  <span v-else>{{ item.label }}</span>
                </template>

                <template #[`item.questions`]="{ item }">
                  <v-tooltip bottom v-if="item.questions.length">
                    <template #activator="{ on, attrs }">
                      <div v-bind="attrs" v-on="on" class="text-truncate" style="max-width: 150px">{{ item.questions.join(', ') }}</div>
                    </template>
                    <span>{{ item.questions.join(', ') }}</span>
                  </v-tooltip>
                </template>

                <template #[`item.approved`]="{ item, index }">
                  <v-icon v-if="item.approved" color="success">{{ icons.mdiCheckCircle }}</v-icon>
                  <div v-else class="d-flex">
                    <v-btn icon x-small color="success" @click="approveItem(country.swof[cat.key], item, index)" :disabled="!item.url"><v-icon>{{icons.mdiCheck}}</v-icon></v-btn>
                    <v-btn icon x-small color="error" class="ml-1" @click="editItem(country.swof[cat.key], item, index)"><v-icon>{{icons.mdiClose}}</v-icon></v-btn>
                  </div>
                </template>

                <template #[`item.actions`]="{ item, index }">
                  <v-btn icon x-small @click="editItem(country.swof[cat.key], item, index)"><v-icon small>{{icons.mdiPencil}}</v-icon></v-btn>
                  <v-btn icon x-small color="grey lighten-1" @click="deleteItem(country.swof[cat.key], item, index)"><v-icon small>{{icons.mdiDelete}}</v-icon></v-btn>
                </template>
              </v-data-table>

              <v-card-actions class="justify-center grey lighten-5 py-1">
                <v-btn text x-small color="primary" @click="addItem(cat.key, idx, country)">
                  <v-icon left x-small>{{ icons.mdiPlus }}</v-icon> Add {{ cat.itemLabel }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </template>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

        
        <v-dialog
            v-model="itemDialog"
            max-width="500px"
        >
            <v-card>
                <alert></alert>
                <v-card-title>
                    <span class="text-h5">Edit Image - {{ editedItem.label }}</span>
                </v-card-title>
                <v-card-text>
                    <v-container>
                        <v-row><v-col
                                cols="12"
                                sm="12"
                                md="12"
                                v-if="editedItem.type == 'CUSTOM'"
                            >
                                <v-text-field
                                v-model="editedItem.label"
                                dense
                                outlined
                                label="Image Label"
                                hide-details
                                ></v-text-field>
                            </v-col>
                            <v-col
                                cols="12"
                                sm="12"
                                md="12"
                            >
                                <v-text-field
                                v-model="editedItem.description"
                                dense
                                outlined
                                label="Description (eg: 'Current', 'Proposed Blue')"
                                hide-details
                                ></v-text-field>
                            </v-col>
                            <v-col
                                cols="12"
                                sm="12"
                                md="12"
                            >
                                <v-text-field
                                v-model="editedItem.details"
                                dense
                                outlined
                                label="Picture Details (Specify codes)"
                                hide-details

                                ></v-text-field>
                            </v-col>
                            
                            <v-col
                                cols="12"
                                sm="12"
                                md="12"
                                v-if="editedItem.type && editedItem.type.startsWith('SKU')"
                            >
                                <v-text-field
                                v-model="editedItem.upc_ean"
                                dense
                                outlined
                                label="UPC/EAN"
                                hide-details
                                ></v-text-field>
                            </v-col>
                            <v-col
                                cols="12"
                                sm="12"
                                md="12"
                            >
                                <v-autocomplete
                                v-model="editedItem.questions"
                                :items="questionOptions"
                                dense
                                outlined
                                label="Questions"
                                hide-details
                                multiple
                                chips
                                small-chips
                                clearable
                                deletable-chips
                                ></v-autocomplete>
                            </v-col>
                            </v-row>
                            <!-- <v-row>
                            
                            <v-col
                                cols="12"
                                sm="6"
                                md="4"
                            >
                                <v-checkbox
                                v-model="editedItem.low_res"
                                dense
                                label="Low Res"
                                hide-details
                                ></v-checkbox>
                            </v-col>
                            <v-col
                                cols="12"
                                sm="6"
                                md="4"
                            >
                                <v-checkbox
                                v-model="editedItem.mid_res"
                                dense
                                label="Mid Res"
                                hide-details
                                ></v-checkbox>
                            </v-col>
                            <v-col
                                cols="12"
                                sm="6"
                                md="4"
                            >
                                <v-checkbox
                                v-model="editedItem.high_res"
                                dense
                                label="High Res"
                                hide-details
                                ></v-checkbox>
                            </v-col>
                            </v-row> -->
                            <v-row>
                                <v-col
                                cols="12"
                                sm="6"
                                md="4"
                                v-if="editedItem.price"
                            >
                                <v-checkbox
                                v-model="editedItem.price"
                                dense
                                label="Price"
                                hide-details
                                disabled
                                ></v-checkbox>
                            </v-col>
                            
                                <v-col
                                cols="12"
                                sm="6"
                                md="4"
                                v-if="editedItem.boxed"
                            >
                                <v-checkbox
                                v-model="editedItem.boxed"
                                dense
                                label="Boxed"
                                hide-details
                                disabled
                                ></v-checkbox>
                            </v-col>
                            
                                <v-col
                                cols="12"
                                sm="6"
                                md="4"
                                v-if="editedItem.reduced"
                            >
                                <v-checkbox
                                v-model="editedItem.reduced"
                                dense
                                label="Reduced Line"
                                hide-details
                                disabled
                                ></v-checkbox>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols=12
                            sm="12" md="12">
                            <v-textarea
                                v-model="editedItem.url"
                                label="URL"
                                dense
                                outlined
                                hint="Link to the image"
                                persistent-hint
                            >

                            </v-textarea>
                        </v-col>
                            <!-- <v-col
                                cols="12"
                                sm="6"
                                md="4"
                            >
                                <v-checkbox
                                v-model="editedItem.jpeg"
                                dense
                                label="jpeg"
                                hide-details
                                ></v-checkbox>
                            </v-col> -->
                            <v-col
                                cols="12"
                                sm="6"
                                md="4"
                            >
                                <v-text-field
                                    v-model="editedItem.num_prints"
                                    dense
                                    outlined
                                    label="Prints"
                                    type="number"
                                    hide-details
                                ></v-text-field>
                            </v-col>
                            <v-col
                                cols="12"
                                sm="6"
                                md="4"
                            >
                                <v-checkbox
                                v-model="editedItem.approved"
                                dense
                                label="Approved"
                                hide-details
                                ></v-checkbox>
                            </v-col>
                            <v-col
                                cols="12"
                                sm="12"
                                md="12"
                            >
                                <v-text-field
                                v-model="editedItem.comments"
                                dense
                                outlined
                                label="Comments/Reasons not approved/..."
                                hide-details

                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>

                <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                    color="blue darken-1"
                    text
                    @click="closeItem"
                >
                    Cancel
                </v-btn>
                <v-btn
                    color="primary"
                    text
                    :loading="swofImageLoading"
                    @click="saveItem"
                >
                    Save
                </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="editDialog"
            max-width="500px"
        >
            <v-card>
                <v-card-title>
                    <span class="text-h5">Edit Images</span>
                </v-card-title>

                <v-card-text>
                    <v-container>
                        <v-form
                            @submit="questionUpdate($event)"
                        >
                            <v-row>
                                <v-col
                                    cols="12"
                                    sm="12"
                                    md="12"
                                >
                                    <v-autocomplete
                                        v-model="questionList"
                                        :items="questionOptions"
                                        dense
                                        outlined
                                        label="Questions"
                                        hide-details
                                        multiple
                                        chips
                                        small-chips
                                        clearable
                                        deletable-chips
                                        >
                                    </v-autocomplete>
                                </v-col>
                                <v-col
                                    cols="12"
                                    sm="12"
                                    md="12"
                                >
                                    <v-text-field
                                        v-model="itemDescription"
                                        label="Description"
                                        dense
                                        outlined
                                        hide-details
                                    >

                                    </v-text-field>
                                </v-col>
                                <v-col
                                    cols="12"
                                    sm="12"
                                    md="12"
                                >
                                    <v-text-field
                                    v-model="itemDetails"
                                    dense
                                    outlined
                                    label="Picture Details (Specify codes)"
                                    hide-details

                                    ></v-text-field>
                                </v-col>
                                <v-col
                                    cols="12"
                                    sm="12"
                                    md="12"
                                >
                                    <v-text-field
                                    v-model="itemNumPrints"
                                    dense
                                    outlined
                                    type="number"
                                    label="Number of prints"
                                    hide-details

                                    ></v-text-field>
                                </v-col>
                                <v-col
                                    cols="12"
                                    sm="12"
                                    md="12"
                                >
                                <v-checkbox
                                    v-model="itemApproved"
                                    label="Approved Selection"
                                    :hint="checkURLExist?'Some elements don\'t have a link to an image.':''"
                                    persistent-hint
                                >

                                </v-checkbox>
                            </v-col>
                            </v-row>
                        </v-form>
                    </v-container>
                </v-card-text>

                <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                    color="blue darken-1"
                    text
                    @click="editDialog = false"
                >
                    Cancel
                </v-btn>
                <v-btn
                    color="primary"
                    text
                    @click="questionUpdate"
                >
                    Save
                </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-model="itemDialogDelete" max-width="550px">
            <v-card>
                <v-card-title class="text-h5">Are you sure you want to delete this item?</v-card-title>
                <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="" small text @click="closeItemDelete">Cancel</v-btn>
                <v-btn color="primary" text small @click="deleteItemConfirm">OK</v-btn>
                <v-spacer></v-spacer>
                </v-card-actions>
            </v-card>
        </v-dialog>
        
        <v-dialog v-model="swofDialogDelete" max-width="550px">
            <v-card>
                <v-card-title class="text-h5">Are you sure you want to delete this SWOF?</v-card-title>
                <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="" small text @click="swofDialogDelete=false">Cancel</v-btn>
                <v-btn color="primary" text small @click="deleteSWOFConfirm" :loading="deleteSWOFLoading">YES</v-btn>
                <v-spacer></v-spacer>
                </v-card-actions>
            </v-card>
        </v-dialog>
        
        <v-dialog v-model="sharepointURLOpen" max-width="550px">
            <v-card>
                <v-card-title class="text-h5">Please input the Sharepoint Shared Link</v-card-title>
                <v-card-text>
                    <v-textarea
                        v-model="sharepointURL.url"
                        label="Sharepoint URL"
                        dense
                        outlined
                    >

                    </v-textarea>
                </v-card-text>
                <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="" small text @click="sharepointURLOpen=false">Cancel</v-btn>
                <v-btn color="primary" text small :loading="swofLoading" @click="updateSWOFSharepoint">Update</v-btn>
                <v-spacer></v-spacer>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-model="notificationOpen" max-width="750px">
            <v-card>
                <v-card-text>
                <v-row>
                    
                        <v-col
                            cols="12"
                            md="8"
                            sm="12"
                        >
                            <v-card>
                                <v-form ref="notificationForm">
                                <v-card-title class="text-h5">Notification</v-card-title>
                                <v-card-text>
                                    <!-- <v-text-field
                                        v-model="notification.destination"
                                        label="To:"
                                        dense
                                        outlined
                                        hide-details
                                    >

                                    </v-text-field> -->
                                    <v-autocomplete
                                        v-model="notification.destination"
                                        :items="toList[tab]"
                                        item-text="name"
                                        item-value="email"
                                        label="To:"
                                        dense
                                        outlined
                                        hide-details="auto"
                                        multiple
                                        small-chips
                                        clearable
                                        deletable-chips
                                        :rules="[validators.required, validators.requiredArray]"
                                    >

                                    </v-autocomplete>
                                </v-card-text>
                                <v-card-text>
                                    <v-text-field
                                        v-model="notification.subject"
                                        label="Subject"
                                        dense
                                        outlined
                                        hide-details="auto"
                                        :rules="[validators.required]"
                                    >

                                    </v-text-field>
                                </v-card-text>
                                <v-card-text>
                                    <v-textarea
                                        v-model="notification.message"
                                        label="Message"
                                        dense
                                        outlined
                                        hide-details="auto"
                                        :rules="[validators.required]"
                                    >

                                    </v-textarea>
                                </v-card-text>
                                </v-form>
                            </v-card>
                        </v-col>
                        
                        <v-col
                            cols="12"
                            md="4"
                            sm="12"
                        >
                        <v-card>
                            <v-card-text>
                                <v-chip
                                    class="ma-2"
                                    label
                                    text-color="white"
                                    :color="message.color"
                                    v-for="(message,i) in messageList[tab]"
                                    :key="i"
                                    @click="notificationClicked(message)"
                                    >
                                    {{ message.label }}
                                    </v-chip>

                            </v-card-text>
                        </v-card>
                    </v-col>

                </v-row>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="" small text @click="cancelNotification">Cancel</v-btn>
                <v-btn
                    color="success"
                    small
                    :loading="swofNotificationLoading"
                    @click="sendNotification">
                    Send
                        <v-icon
                        right
                        small
                        >
                            {{ icons.mdiSendOutline }}
                        </v-icon>
                </v-btn>
                <v-spacer></v-spacer>
            </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
  
</template>

<script>
import { ref, onMounted, watch, computed, nextTick } from '@vue/composition-api'
import { required, requiredArray } from '@core/utils/validation'
import { isObjectEmpty, goToLink  } from '@core/utils'
import { 
    mdiPencil, 
    mdiDelete,
    mdiCheck, mdiClose, mdiNumericPositive1, 
    mdiSendOutline,
    mdiFileExcelOutline,
    mdiDeleteOutline,
    mdiAutoFix,
    mdiOpenInNew,
    mdiPlus,
    mdiImageMultiple,
    mdiCheckCircle
} from '@mdi/js'
import _ from 'lodash-es'

import useProject from '@/views/apps/project/useProject'
import useSurvey from '@/views/apps/survey/useSurvey'
import Alert from '@/views/components/alert/Alert.vue'

export default {
    components:{
        Alert
    },
    props: {
        projectData: {
            type: Object,
            required: true,
        },
        questionnaires: {
            type: Array,
            required: false,
            default: ()=>[]
        }
    },
    setup(props, { emit }){
        const questionOptions = ref([])
        const toList = ref([])
        const messageList = ref([])
        
        const {
            deleteSWOFLoading,
            questionnaire,
            swof,
            swofLoading,
            swofImageLoading,
            swofNotificationLoading,
            swofImage,

            addSWOFImage,
            updateSWOFImage,
            deleteSWOFImage,
            fetchSWOF,
            addSWOF,
            updateSWOF,
            updateSWOFURL,
            sendSWOFNotification,
            fetchQuestionnaire,
            swofQuestionList,
            downloadSWOF,
            deleteSWOF
        } = useProject()

        const {
            listQuestionLabel
        } = useSurvey()

        
        const localProjectData = ref({})
        const tab = ref(null)
        const countries = ref([])

        const imageList = ref([])
        const itemDialog = ref(false)
        const itemDialogDelete = ref(false)
        const editDialog = ref(false)
        const swofDialogDelete = ref(false)
        const editedList = ref([])
        const questionList = ref([])
        const itemApproved = ref(false)
        const itemDescription = ref('')
        const itemDetails = ref('')
        const itemNumPrints = ref(0)
        const editedIndex = ref(-1)
        const editedItem = ref({
            label: "",
            description:"",
            products:[],
            questions:[],
            lifesize_percent:100,
            jpeg:true,
            num_prints:0,
            price:false,
            boxed:false,
            details:""}
        )
        const defaultItem = ref({
            label: "",
            description:"",
            products:[],
            questions:[],
            lifesize_percent:100,
            jpeg:true,
            num_prints:0,
            price:false,
            boxed:false,
            details:""
        })
        const sharepointURL = ref({})
        const sharepointURLOpen = ref(false)

        const notificationOpen = ref(false)
        const notification = ref({})
        
        const notificationForm = ref(null)

        const imageHeaders = [
            {
                text: 'Label',
                align: 'start',
                value: 'label',
            },
            { text: 'Details', value: 'details' },
            { text: 'Questions', value: 'questions' },
            { text: 'Approved', value: 'approved', sortable: false },
            { text: 'Actions', value: 'actions', sortable: false },
        ]
        const swofCategories = [
            { key: 'shelves', label: 'Shelf Images', urlKey: 'shelf_url', selectionKey: 'shelvesSelected', spType: 'SHELF', itemLabel: 'Shelf', duplicateAction: { suffix: 'Boxed', label: 'Add Boxed' } },
            { key: 'skuTests', label: 'SKU Test Images', urlKey: 'sku_test_url', selectionKey: 'skuTestsSelected', spType: 'SKUTEST', itemLabel: 'Test SKU', duplicateAction: { suffix: 'P', label: 'Add Price' } },
            { key: 'skuCompetitors', label: 'SKU Competitor Images', urlKey: 'sku_comp_url', selectionKey: 'skuCompetitorsSelected', spType: 'SKUCOMP', itemLabel: 'Comp SKU', duplicateAction: { suffix: 'P', label: 'Add Price' } },
            { key: 'lines', label: 'Line Test Images', urlKey: 'line_test_url', selectionKey: 'linesSelected', spType: 'LINETEST', itemLabel: 'Line', duplicateAction: { suffix: 'P', label: 'Add Price' } },
            { key: 'lineCompetitors', label: 'Line Competitors Images', urlKey: 'line_comp_url', selectionKey: 'lineCompetitorsSelected', spType: 'LINECOMP', itemLabel: 'Line Competitors', duplicateAction: { suffix: 'P', label: 'Add Price' } },
            { key: 'aidedRecall', label: 'Aided Recall', urlKey: 'ar_url', selectionKey: 'aidedRecallSelected', spType: 'AR', itemLabel: 'AR' },
            { key: 'concepts', label: 'Concepts', urlKey: 'concept_url', selectionKey: 'conceptsSelected', spType: 'CONCEPT', itemLabel: 'Concept', condition: 'innocompass', duplicateAction: { suffix: 'Long', label: 'Add Long' }},
            { key: 'custom', label: 'Custom Images', urlKey: 'custom_url', selectionKey: 'customSelected', spType: 'CUSTOM', itemLabel: 'Custom' },
        ]

        const folderLinks = [
            { key: 'stim_guide', urlKey: 'stim_guide_url', label: 'Stim. Guide', color: 'info', type: 'STIM' },
            { key: 'concepts', urlKey: 'concepts_url', label: 'Concepts Folder', color: 'error', type: 'CONCEPTS' },
            { key: 'individuals', urlKey: 'individuals_url', label: 'Individuals', color: 'warning', type: 'IND' },
        ]

        const generatorFields = [
            { model: 'phase', label: 'Shelves Count', hint: 'Total shelf images' },
            { model: 'sku_test_count', label: 'Test SKU', hint: 'Test SKUs per phase' },
            { model: 'sku_comp_count', label: 'Comp SKU', hint: 'Competitor SKUs' },
            { model: 'line_test_count', label: 'Line Test', hint: 'Test lines' },
            { model: 'line_comp_count', label: 'Line Comp', hint: 'Line competitors' },
            { model: 'ar_count', label: 'AR Count', hint: 'Aided recall count' },
        ]


        const initNotification = () => {
            if (notificationForm.value != null) notificationForm.value.resetValidation()
            notification.value = {
                job_number:localProjectData.value.job_number,
                country_code:"",
                destination: [],
                subject: "",
                message: ""
            }
        }

        const initialize = () => {
            localProjectData.value = JSON.parse(JSON.stringify(props.projectData))
            initNotification()
            notification.value.job_number = localProjectData.value.job_number
            countries.value = []
            toList.value = []
            messageList.value = []
            localProjectData.value.countries.forEach((country, countryIdx) => {
                countries.value.push( {
                    id: country.id,
                    name: country.country_detail.name,
                    phase: country.phase,
                    country_code: country.country_detail.code,
                    swof:{
                        shelves: [],
                        shelf_url: "",
                        shelvesSelected:[],
                        skuTests: [],
                        skuTestsSelected:[],
                        skuCompetitors: [],
                        skuCompetitorsSelected:[],
                        lines: [],
                        linesSelected:[],
                        lineCompetitors: [],
                        lineCompetitorsSelected:[],
                        aidedRecall: [],
                        aidedRecallSelected:[],
                        custom: [],
                        customSelected:[]
                    },
                    questionnaire: country.questionnaire
                })
                messageList.value.push([])
                messageList.value[countryIdx] = [
                    {
                        label: "SWOF is ready!",
                        message:`Hello,\n\nThe SWOF has now been completed.\nPlease proceed with creating the images according to the information provided.\nIf any information is missing or unclear, please feel free to reach out.\n\nThank you!\n\nLink: ${process.env.VUE_APP_MSAL_REDIRECT}/projects/${localProjectData.value.job_number}/swof`,
                        subject:`${localProjectData.value.job_number} - ${country.country_detail.name} - SWOF is ready!`,
                        color:"primary",
                        type: "swof_ready"
                    },
                    {
                        label: "Images are ready!",
                        message:`Hello,\n\nThe images have now been created and uploaded to the SWOF.\nPlease proceed with approving the images before notifying the programming team.\nIn case of mistakes or missing images, please feel free to reach out.\n\nThank you!\n\nLink: ${process.env.VUE_APP_MSAL_REDIRECT}/projects/${localProjectData.value.job_number}/swof`,
                        subject:`${localProjectData.value.job_number} - ${country.country_detail.name} - Images are ready!`,
                        color:"warning",
                        type: "img_ready"
                    },
                    {
                        label: "Images are approved!",
                        message:`Hello,\n\nThe images are now approved and ready.\nPlease proceed  with programming the survey.\nIn case of mistakes or missing images, please feel free to reach out.\n\nThank you!\n\nLink: ${process.env.VUE_APP_MSAL_REDIRECT}/projects/${localProjectData.value.job_number}/swof`,
                        subject:`${localProjectData.value.job_number} - ${country.country_detail.name} - Images are approved!`,
                        color:"info",
                        type: "img_approved"
                    },
                    {
                        label: "Images need rework.",
                        message:"Hello,\n\A few images need to be rework.\nPlease see below the different comments for each image.",
                        subject:`${localProjectData.value.job_number} - ${country.country_detail.name} - Images need rework.`,
                        color:"error",
                        type: "img_rework"
                    }
                ]

                if (localProjectData.value.service_type.code == 'innocompass'){
                    const len = countries.value.length
                    countries.value[len-1]['concepts'] = []
                    countries.value[len-1]['conceptsSelected'] = []
                }
                var swofNotificationPotentialDestination = []
                
                if (country.admin.length > 0){
                    swofNotificationPotentialDestination.push(
                        { header: 'Admin' }
                    )
                    country.admin.map(admin => swofNotificationPotentialDestination.push(
                        {name: admin.name, group: 'Admin', email: admin.email }
                    ))
                }
                if (country.project_management.length > 0){
                    swofNotificationPotentialDestination.push(
                        { header: 'PM' }
                    )
                    country.project_management.map(project_management => swofNotificationPotentialDestination.push(
                        {name: project_management.name, group: 'PM', email: project_management.email }
                    ))
                }
                if (country.programming.length > 0){
                    swofNotificationPotentialDestination.push(
                        { header: 'Prgm' }
                    )
                    country.programming.map(programming => swofNotificationPotentialDestination.push(
                        {name: programming.name, group: 'Prgm', email: programming.email }
                    ))
                }
                if (country.studio.length > 0){
                    swofNotificationPotentialDestination.push(
                        { header: 'Studio' },
                        {name: 'Studio Team', group: 'Studio', email: 'StudioTeam@prs-invivo-group.com' }
                    )
                    
                }
                toList.value.push(swofNotificationPotentialDestination)
            })
            initCountrySwof(0,  countries.value[0])
        }

        const initCountrySwof = async (index, country) => {

            const response = await fetchSWOF({project_country: country.id})

            editedIndex.value = -1
            if (index >= 0 & index < localProjectData.value.countries.length) {
                if (countries.value[index].questionnaire) {
                    await fetchQuestionnaire(countries.value[index].questionnaire)
                    questionOptions.value = swofQuestionList.value.filter(question => listQuestionLabel(questionnaire.value.json.pages).includes(question.label) > 0).map(question => question.label)
                }

                // countries.value[0].swof.shelves = swofShelves
            }
        }
        // FILTERED CATEGORIES based on service type
        const activeCategories = computed(() => {
        return swofCategories.filter(cat => {
            if (!cat.condition) return true;
            if (cat.condition === 'innocompass') return props.projectData.service_type.code === 'innocompass';
            return true;
        })
        })

        const isAnySelected = (swof, key) => {
        const selectionKey = swofCategories.find(c => c.key === key).selectionKey;
        return swof[selectionKey] && swof[selectionKey].length > 0;
        }

        const handleFolderClick = (swof, urlKey, type) => {
            const url = swof[urlKey];

            if (url && typeof url === 'string' && url.trim() !== '') {
                window.open(url, '_blank');
            } else {
                openSWOFSharepoint(swof.id, type);
            }
        };

        const generateSWOF = async (countryIdx) => {
            const country = countries.value[countryIdx]

            var payload = {
                'project_country': country.id,
                'shelf_count': country.phase,
                'sku_test_count': country.sku_test_count,
                'sku_comp_count': country.sku_comp_count,
                'line_test_count': country.line_test_count,
                'line_comp_count': country.line_comp_count,
                'ar_count': country.ar_count,
                'custom_count': country.custom_count
            }

            var initSWOF = {}
            initSWOF['shelves'] = createShelfDesign(0, country.phase)
            initSWOF['skuTests'] = createSkuTests(country, 0, country.sku_test_count, 0, country.phase)
            initSWOF['skuCompetitors'] = createSkuCompetitors(0, country.sku_comp_count)
            initSWOF['lines'] = createLines(country, 0, country.line_test_count, 0, country.phase)
            initSWOF['lineCompetitors'] = createLineCompetitors(0, country.line_comp_count)
            initSWOF['custom'] = createCustom(0, country.custom_count)
            initSWOF['aidedRecall'] = createAidedRecall(0, country.ar_count)
            if (localProjectData.value.service_type.code == 'innocompass') {
                initSWOF['concepts'] = createConcepts(0, country.concept_count)
                payload['concept_count'] = country.concept_count
            }

            payload['initial_swof'] = initSWOF
            const resp = await addSWOF(payload)
        }


        const editItem = (list, item, index) => {
            editedIndex.value = index
            editedItem.value = Object.assign({}, item)
            imageList.value = list
            itemDialog.value = true
        }

        const deleteItem = (list, item, index) => {
            editedIndex.value = index
            editedItem.value = Object.assign({}, item)
            imageList.value = list
            itemDialogDelete.value = true
        }

        const deleteItemConfirm = () => {
            imageList.value.splice(editedIndex.value, 1)
            deleteSWOFImage(editedItem.value)
            closeItemDelete()
        }

        const closeItem = () => {
            itemDialog.value = false
            nextTick(() => {
            editedItem.value = Object.assign({}, defaultItem.value)
            editedIndex.value = -1
            })
        }

        const closeItemDelete = () => {
            itemDialogDelete.value = false
            nextTick(() => {
            editedItem.value = Object.assign({}, defaultItem.value)
            editedIndex.value = -1
            })
        }

        const deleteSWOFConfirmation = () => {
            swofDialogDelete.value = true

        }

        const deleteSWOFConfirm = async () => {
            await deleteSWOF(countries.value[tab.value].swof.id)
            swofDialogDelete.value = false
            initialize()
        }

        const addItem = async (type, index, country) => {
            const config = swofCategories.find(c => c.key === type);
            const updateSWOFImages = { id: country.swof.id, update_swof: {} };
            

            if (type == 'shelves') {
                const shelves = createShelfDesign(country.swof.shelf_count, country.swof.shelf_count+1)
                country.swof['shelves'].push(...shelves)

                // When new shelf/Phase image added then add new test skus and test line
                const skuTests = createSkuTests(country, 0, country.swof.sku_test_count,country.swof.shelf_count, country.swof.shelf_count+1)
                country.swof['skuTests'].push(...skuTests)
                updateSWOFImages['update_swof']['skuTests'] = skuTests
                
                const lines = createLines(country, 0, country.swof.line_test_count, country.swof.shelf_count, country.swof.shelf_count+1)
                country.swof['lines'].push(...lines)
                updateSWOFImages['update_swof']['lines'] = lines

                country.swof.shelf_count += 1
                updateSWOFImages['update_swof']['shelves'] = shelves
                updateSWOFImages.shelf_count = country.swof.shelf_count

            }
            if (type == 'skuTests') {
                const skuTests = createSkuTests(country, country.swof.sku_test_count, country.swof.sku_test_count+1, 0, country.swof.shelf_count)
                country.swof['skuTests'].push(...skuTests)
                country.swof.sku_test_count += 1
                updateSWOFImages['update_swof']['skuTests'] = skuTests
                updateSWOFImages.sku_test_count = country.swof.sku_test_count
            }
            if (type == 'skuCompetitors') {
                const skuCompetitors = createSkuCompetitors(country.swof.sku_comp_count, country.swof.sku_comp_count+1)
                country.swof['skuCompetitors'].push(...skuCompetitors)
                country.swof.sku_comp_count += 1
                updateSWOFImages['update_swof']['skuCompetitors'] = skuCompetitors
                updateSWOFImages.sku_comp_count = country.swof.sku_comp_count
            }
            if (type == 'lines') {
                const lines = createLines(country, country.swof.line_test_count, country.swof.line_test_count+1, 0, country.swof.shelf_count)
                country.swof['lines'].push(...lines)
                country.swof.line_test_count += 1
                updateSWOFImages['update_swof']['lines'] = lines
                updateSWOFImages.line_test_count = country.swof.line_test_count
            }
            if (type == 'lineCompetitors') {
                const lineCompetitors = createLineCompetitors(country.swof.line_comp_count, country.swof.line_comp_count+1)
                country.swof['lineCompetitors'].push(...lineCompetitors)
                country.swof.line_comp_count += 1
                updateSWOFImages['update_swof']['lineCompetitors'] = lineCompetitors
                updateSWOFImages.line_comp_count = country.swof.line_comp_count
            }
            if (type == 'aidedRecall') {
                const aidedRecall = createAidedRecall(country.swof.ar_count, country.swof.ar_count+1)
                country.swof['aidedRecall'].push(...aidedRecall)
                country.swof.ar_count += 1
                updateSWOFImages['update_swof']['aidedRecall'] = aidedRecall
                updateSWOFImages.ar_count = country.swof.ar_count
            }
            if (type == 'concepts') {
                const concepts = createConcepts(country.swof.concept_count, country.swof.concept_count+1)
                country.swof['concepts'].push(...concepts)
                country.swof.concept_count += 1
                updateSWOFImages['update_swof']['concepts'] = concepts
                updateSWOFImages.concept_count = country.swof.concept_count
            }
            if (type == 'custom') {
                const custom = createCustom(country.swof.custom_count, country.swof.custom_count+1)
                country.swof['custom'].push(...custom)
                country.swof.custom_count += 1
                updateSWOFImages['update_swof']['custom'] = custom
                updateSWOFImages.custom_count = country.swof.custom_count
            }

            await updateSWOF(updateSWOFImages)

        }

        const openSWOFSharepoint = (id, type) => {
            sharepointURL.value.id = id
            sharepointURL.value.type = type
            sharepointURLOpen.value = true
        }

        const updateSWOFSharepoint = () => {
            
            var updateSWOFURLImages = {
                id:sharepointURL.value.id,
                update_url:{
                    type: sharepointURL.value.type,
                    sharingURL: sharepointURL.value.url
                }
            }
            updateSWOFURL(updateSWOFURLImages)
        }

        const saveItem = () => {
            if (editedIndex.value > -1) {
                Object.assign(imageList.value[editedIndex.value], editedItem.value)
                updateSWOFImage(editedItem.value)
            } else {
                imageList.value.push(editedItem.value)
                addSWOFImage(editedItem.value)
            }
        }
        const shelfDesignCount = ref(null)
        const createShelfDesign = (lowerLimit, upperLimit) => {
            
            var shelves = []
            var idx = 0
            // for (var idx = 0; idx < countries.value[countryIdx].shelf_design; idx++) {
                for(var i=lowerLimit; i<upperLimit; i++) {
                    const shelfImage = {
                        label: `SHELF${idx+1}${String.fromCharCode(64 + i+1) }`,
                        description:idx == 0 ? `SHELF ${idx+1} Design ${i+1}`:`SHELF ${idx+1} Design ${i+1}`,
                        questions:[],
                        lifesize_percent:100,
                        jpeg:true,
                        num_prints:0,
                        price:false,
                        details:"",
                        approved:false,
                        url:""
                    }
                    shelves.push(shelfImage)
                }
            // }
            return shelves
        }

        const skuTestDialog = ref(false)
        const createSkuTests = (country=false, lowerLimit, upperLimit, phaseLowerLimit, phaseUpperLimit) => {
           var skuTests = []
            for (var idx = lowerLimit; idx < upperLimit; idx++) {
                for(var i=phaseLowerLimit; i<phaseUpperLimit; i++) {
                    const skuImage = {
                        label: `SKU${idx+1}${String.fromCharCode(64 + i+1) }`,
                        description:idx == 0 ? `SKU ${idx+1} (Key SKU) Phase ${i+1}`:`SKU ${idx+1} Phase ${i+1}`,
                        questions:[],
                        lifesize_percent:100,
                        jpeg:true,
                        num_prints:0,
                        price:false,
                        details:"",
                        approved:false,
                        url:""
                    }
                    skuTests.push(skuImage)
                }
            }
            return skuTests
        }


        const createSkuCompetitors = ( lowerLimit, upperLimit) => {

            var skuCompetitors = []
            for (var idx = lowerLimit; idx < upperLimit; idx++) {
                const skuImage = {
                    label: `SKUX${idx+1}`,
                    description:`Competitor ${idx+1}`,
                    questions:[],
                    lifesize_percent:100,
                    jpeg:true,
                    num_prints:0,
                    price:false,
                    details:"",
                    approved:false,
                    url:""
                }
                skuCompetitors.push(skuImage)
            }
            return skuCompetitors
        }

        const createLines = (country=null, lowerLimit, upperLimit, phaseLowerLimit, phaseUpperLimit) => {
            var lines = []
            for(var i=phaseLowerLimit; i<phaseUpperLimit; i++) {
                for (var idx = lowerLimit; idx < upperLimit; idx++) {
                    const prefix = `${String.fromCharCode(64 + i+1) }${String.fromCharCode(64 + i+1) }`
                    const skuImage = {
                        label: `${prefix}${idx+1}`,
                        description:`Test Line ${idx+1} - Phase ${i+1}`,
                        questions:[],
                        lifesize_percent:100,
                        jpeg:true,
                        num_prints:0,
                        price:false,
                        details:"",
                        approved:false,
                        url:""
                    }
                    lines.push(skuImage)
                }
            }
            return lines
        }

        

        const createLineCompetitors = (lowerLimit, upperLimit) => {
            var lineCompetitors = []
            for (var idx = lowerLimit; idx < upperLimit; idx++) {
                const skuImage = {
                    label: `XX${idx+1}`,
                    description:`Competitor Line ${idx+1}`,
                    questions:[],
                    lifesize_percent:100,
                    jpeg:true,
                    num_prints:0,
                    price:false,
                    details:"",
                    approved:false,
                    url:""
                }
                lineCompetitors.push(skuImage)
            }
            return lineCompetitors
        }

        

        const createAidedRecall = (lowerLimit, upperLimit) => {
            var ar = []
            for (var idx = lowerLimit; idx < upperLimit; idx++) {
                const skuImage = {
                    label: `AR${idx+1}`,
                    description:`Aided Recall ${idx+1}`,
                    questions:[],
                    lifesize_percent:100,
                    jpeg:true,
                    num_prints:0,
                    price:false,
                    details:"",
                    approved:false,
                    url:""
                }
                ar.push(skuImage)
            }
            return ar
        }

        const createConcepts = (lowerLimit, upperLimit) => {
            var concepts = []
            for (var idx = lowerLimit; idx < upperLimit; idx++) {
                const suffix = `${String.fromCharCode(64 + idx+1) }`
                const conceptImage = {
                    label: `Short_${suffix}`,
                    description:`Concept Short/Long Version ${suffix}`,
                    questions:[],
                    lifesize_percent:100,
                    jpeg:true,
                    num_prints:0,
                    details:"",
                    approved:false,
                    url:""
                }
                concepts.push(conceptImage)
            }
            return concepts

        }

        const createCustom = (lowerLimit, upperLimit) => {
            var custom = []
            for (var idx = lowerLimit; idx < upperLimit; idx++) {
                const customImage = {
                    label: `Custom_${idx+1}`,
                    description:`Custom Version ${idx+1}`,
                    questions:[],
                    lifesize_percent:100,
                    jpeg:true,
                    num_prints:0,
                    details:"",
                    approved:false,
                    url:""
                }
                custom.push(customImage)
            }
            return custom

        }

        

        const editSelected = (selected) => {
            editDialog.value = true
            editedList.value = selected

        }

        const approveItem = (list, item, index) => {
            item.approved = true

            
            editedIndex.value = index
            editedItem.value = Object.assign({}, item)
            imageList.value = list
            saveItem()
        }

        const disapproveItem = (list, item, index) => { 
            item.approved = false
        }

        const questionUpdate = (event) => {

            if (event) event.preventDefault()

            const questions = JSON.parse(JSON.stringify(questionList.value))

            imageList.value = editedList.value
            editedList.value.map((image, index) => {
                if (editedList.value.some(selectedImage => selectedImage.label.includes(image.label))){
                    image.questions = questions
                    if (itemDescription.value.length !== 0) image.description = itemDescription.value
                    if (itemDetails.value.length !== 0) image.details = itemDetails.value
                    if (itemNumPrints.value >= 0) image.num_prints = itemNumPrints.value
                    
                    editedItem.value = image
                    editedIndex.value = index
                    saveItem()
                    if (itemApproved.value && image.url != '') image.approved = true
                }
            })
            editedList.value = []
            questionList.value = []
            itemApproved.value = false
            itemDescription.value = ''
            itemDetails.value = ''
            itemNumPrints.value = 0
            editDialog.value = false
        }

        const duplicateSelection = async (country, type, suffix, selectionList) => {
            var updateSWOFImages = {
                id:country.swof.id,
                update_swof:{}
            }
            const duplicatedList =  JSON.parse(JSON.stringify(selectionList));
            
            duplicatedList.map(image => {
                if (type == 'concepts') {
                    image.label = image.label.replace('Short', suffix)
                } else {
                    image.label += `_${suffix}`
                }
                image.url = ''
                image.approved = false
                image.boxed = suffix == 'Boxed' ? true : false
                image.price = suffix == 'P' ? true : false
                image.reduced = suffix == 'R' ? true : false
                delete image['id']
                delete image['swof']
                delete image['type']
                delete image['approved_by']
            })
            const swofImageList = duplicatedList.filter(image => !country.swof[type].map(swofImg => swofImg.label).includes(image.label))
            updateSWOFImages.update_swof[type] = swofImageList
            country.swof[type].push(...swofImageList)


            if (type == 'shelves') {updateSWOFImages.update_swof.shelf_count += swofImageList.length; country.swof.shelf_count += swofImageList.length;}
            if (type == 'skuTests') {updateSWOFImages.update_swof.sku_test_count += swofImageList.length; country.swof.sku_test_count += swofImageList.length;}
            if (type == 'skuCompetitors') {updateSWOFImages.update_swof.sku_comp_count += swofImageList.length; country.swof.sku_comp_count += swofImageList.length;}
            if (type == 'lines') {updateSWOFImages.update_swof.line_test_count += swofImageList.length; country.swof.line_test_count += swofImageList.length;}
            if (type == 'lineCompetitors') {updateSWOFImages.update_swof.line_comp_count += swofImageList.length; country.swof.line_comp_count += swofImageList.length;}
            if (type == 'aidedRecall') {updateSWOFImages.update_swof.ar_count += swofImageList.length; country.swof.ar_count += swofImageList.length;}
            if (type == 'concepts') {updateSWOFImages.update_swof.concept_count += swofImageList.length; country.swof.concept_count += swofImageList.length;}
            
            await updateSWOF(updateSWOFImages)

            selectionList = []

        }


        const setARTestDesign =  async (country, selectionList) => {
            var updateSWOFImages = {
                id:country.swof.id,
                update_swof:{}
            }
            const duplicatedList =  JSON.parse(JSON.stringify(selectionList));
            const arTestLength = duplicatedList.length
            for (var idx = 0; idx < arTestLength; idx++) {
                for(var i=0; i<country.swof.shelf_count; i++) {
                    if (i == 0){
                        duplicatedList[idx].label += "_A"
                    } else {
                        const newARImage = JSON.parse(JSON.stringify(duplicatedList[idx]));
                        newARImage.label = duplicatedList[idx].label.slice(0, -1) + String.fromCharCode(64 + i+1)
                        delete newARImage['id']
                        delete newARImage['swof']
                        delete newARImage['type']
                        delete newARImage['approved_by']
                        duplicatedList.push(newARImage)
                    }
                }
            }
            const swofImageList = duplicatedList.filter(image => !country.swof['aidedRecall'].map(swofImg => swofImg.label).includes(image.label))
            updateSWOFImages.update_swof['aidedRecall'] = swofImageList
            country.swof['aidedRecall'].push(...swofImageList)
            await updateSWOF(updateSWOFImages)

            selectionList = []
        }

        const checkURLExist = computed(() => {
            return editedList.value.some((image) => image.url == '') || false
        })

        const notificationClicked = (message) => {
            notification.value.message = message.message
            notification.value.subject = message.subject

            if (message.type == 'img_rework') {
                let image_notes = ""
                Object.entries(countries.value[tab.value]['swof']).forEach(([key, value]) => {
                    if(typeof value == 'object' && value != null) {
                        const rework = value.filter(img => img.comments && img.comments != "").map(img => {
                            image_notes += `${img.label}: ${img.comments}\n`
                            return `${img.label}: ${img.comments}\n`
                        })
                    }
                })
                notification.value.message += `\n\n${image_notes}\nIn case of mistakes, please feel free to reach out.\n\nThank you!\n\nLink: ${process.env.VUE_APP_MSAL_REDIRECT}/projects/${localProjectData.value.job_number}/swof`
            }
        }

        const sendNotification = async () => {
            
            const isStepValid = notificationForm.value.validate()
            if (isStepValid) {
                notification.value.country_code = countries.value[tab.value].country_code
                await sendSWOFNotification(notification.value)
                cancelNotification()
            }
            
        }
        const cancelNotification = () => {
            notificationOpen.value=false
            initNotification()
        }

        const areAllValuesEmpty  = (obj) => {
            return Object.values(obj).every(value => {
                // Check if value is an empty array
                const isEmptyArray = Array.isArray(value) && value.length === 0;
                
                // Check if value is an empty string
                const isEmptyString = value === "";

                return isEmptyArray || isEmptyString;
            });
        };


        onMounted(() => {
            initialize()
        })

        watch(
            swof,
            (newVal, oldVal) => {
                
                if (newVal.length > 0) {
                    _.forEach(newVal[0], (el, key) => {
                        countries.value[tab.value].swof[key] = el
                    })
                } else 
                if (!isObjectEmpty(newVal)) {
                    _.forEach(newVal, (el, key) => {
                        countries.value[tab.value].swof[key] = el
                    })
                } 

            }
        )

        watch(
            itemDialog,
            (newVal, oldVal)=>{
                newVal || closeItem()
            }
        )
        
        watch(
            itemDialogDelete,
            (val)=>{
                val || closeItemDelete()
            }
        )

        watch(
            () => props.projectData,
            () => {
                initialize()
            }
        )

        watch(
            swofLoading,
            (newVal, oldVal) => {
                if (!newVal) {
                    sharepointURL.value = {}
                    sharepointURLOpen.value = false
                    initNotification()
                }
            }
        )

        watch(
            notificationOpen,
            (newVal, oldVal) => {
                if(!newVal) initNotification()
            }
            
        )

        return {
            initialize,
            areAllValuesEmpty,
            localProjectData,
            tab,
            countries,
            swof,
            editedItem,
            addItem,
            itemDialog,
            itemDialogDelete,
            swofDialogDelete,
            closeItem,
            closeItemDelete,
            editDialog,
            editSelected,
            editItem,
            duplicateSelection,
            setARTestDesign,
            deleteItem,
            deleteItemConfirm,
            closeItem,
            closeItemDelete,
            saveItem,
            approveItem,
            initCountrySwof,
            questionUpdate,
            questionOptions,
            questionList,
            editedList,
            itemApproved,
            itemDescription,
            itemDetails,
            itemNumPrints,
            imageHeaders,
            skuTestDialog,
            shelfDesignCount,
            createSkuTests,
            createShelfDesign,
            createSkuCompetitors,
            createLines,
            createLineCompetitors,
            createConcepts,
            generateSWOF,
            swofImageLoading,
            swofLoading,
            sharepointURL,
            sharepointURLOpen,
            openSWOFSharepoint,
            updateSWOFSharepoint,
            checkURLExist,
            notificationOpen,
            notification,
            swofNotificationLoading,
            sendNotification,
            toList,
            messageList,
            notificationClicked,
            cancelNotification,
            notificationForm,
            goToLink,
            downloadSWOF,
            deleteSWOFConfirmation,
            deleteSWOFConfirm,
            deleteSWOFLoading,
            activeCategories, folderLinks, generatorFields, isAnySelected, handleFolderClick,

            icons: {
                mdiPencil,
                mdiDelete,
                mdiDeleteOutline,
                mdiAutoFix,
                mdiCheck,
                mdiClose,
                mdiNumericPositive1,
                mdiSendOutline,
                mdiFileExcelOutline,
                mdiOpenInNew,
                mdiPlus,
                mdiImageMultiple,
                mdiCheckCircle
            },

            validators: {
                required,
                requiredArray
            }
        }
    }

}
</script>

<style scoped>
.shadow-sm { box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important; }
.border { border: 1px solid #e0e0e0 !important; }
.border-bottom { border-bottom: 1px solid #e0e0e0 !important; }
.max-width-800 { max-width: 800px; }

</style>